import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertCircle, Loader2, CalendarDays, Package } from 'lucide-react';
import BACKEND_URL from '../lib/backendUrl';

const API = `${BACKEND_URL}/api/purchase-orders/public`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const money = (n, c) => { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c || 'AUD' }).format(n || 0); } catch (e) { return `${c} ${(n || 0).toFixed(2)}`; } };

const Done = ({ po, conf }) => (
  <div className="text-center py-6" data-testid="po-confirm-done">
    <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
    <h2 className="text-2xl font-bold mt-4">Order confirmed</h2>
    <p className="text-slate-600 mt-2">Thank you. {po.company.name} has been notified that {po.po_number} is confirmed{conf.delivery_date ? ` for delivery on ${fmt(conf.delivery_date)}` : ''}.</p>
    <p className="text-xs text-slate-400 mt-4">Confirmed {new Date(conf.confirmed_at + (conf.confirmed_at.endsWith('Z') ? '' : 'Z')).toLocaleString('en-AU')}</p>
  </div>
);

export default function PublicPOConfirm() {
  const { token } = useParams();
  const [po, setPo] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ name: '', delivery_date: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [conf, setConf] = useState(null);

  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => {
      setPo(r.data);
      setConf(r.data.confirmation);
      setForm((f) => ({ ...f, delivery_date: (r.data.expected_delivery_date || '').slice(0, 10) }));
    }).catch((e) => setErr(e.response?.data?.detail || 'This link is not valid.'));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try { setConf((await axios.post(`${API}/${token}/confirm`, form)).data.confirmation); } catch (e) { setErr(e.response?.data?.detail || 'Could not confirm the order. Please try again.'); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="po-confirm-page">
      <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {!po && !err && <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
        {err && !po && <div className="p-10 text-center" data-testid="po-confirm-error"><AlertCircle className="w-10 h-10 text-red-500 mx-auto" /><p className="mt-3 text-slate-700">{err}</p></div>}
        {po && (
          <>
            <div className="bg-slate-900 text-white px-6 py-5 flex items-center gap-5">
              {po.company.logo && <img src={po.company.logo} alt="" className="h-12 max-w-[140px] object-contain bg-white rounded p-1" />}
              <div><p className="text-[11px] tracking-[0.2em] text-slate-400">PURCHASE ORDER</p><p className="text-xl font-bold" data-testid="po-confirm-number">{po.po_number}</p><p className="text-sm text-slate-300">from {po.company.name}</p></div>
            </div>
            <div className="p-6">
              {conf ? <Done po={po} conf={conf} /> : (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><p className="text-slate-500">Order date</p><p className="font-semibold">{fmt(po.order_date)}</p></div>
                    <div><p className="text-slate-500">Requested delivery</p><p className="font-semibold">{fmt(po.expected_delivery_date)}</p></div>
                  </div>
                  {po.delivery_instructions && <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-sm"><b className="text-amber-900">Delivery instructions:</b> {po.delivery_instructions}</div>}
                  <div className="mt-5 border rounded-lg divide-y text-sm" data-testid="po-confirm-lines">
                    {po.lines.map((l, i) => (
                      <div key={i} className="flex justify-between gap-3 px-3 py-2">
                        <span className="flex items-center gap-2 min-w-0"><Package className="w-4 h-4 text-slate-400 shrink-0" /><span className="truncate">{l.description}{l.backorder ? ' (back order)' : ''}</span></span>
                        <span className="whitespace-nowrap text-slate-600">{l.quantity} {l.unit} · <b className="text-slate-900">{l.backorder ? '—' : money(l.total, po.currency)}</b></span>
                      </div>
                    ))}
                    <div className="flex justify-between px-3 py-2 bg-slate-50 font-bold"><span>Total {po.currency}</span><span data-testid="po-confirm-total">{money(po.total, po.currency)}</span></div>
                  </div>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <label className="text-sm"><span className="text-slate-600">Your name</span><input className="mt-1 w-full border rounded-md h-10 px-3" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Optional" data-testid="po-confirm-name" /></label>
                    <label className="text-sm"><span className="text-slate-600 flex items-center gap-1"><CalendarDays className="w-4 h-4" />Delivery date you can meet</span><input type="date" className="mt-1 w-full border rounded-md h-10 px-3" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="po-confirm-date" /></label>
                  </div>
                  <label className="text-sm block mt-4"><span className="text-slate-600">Note to {po.company.name} (optional)</span><textarea rows={3} className="mt-1 w-full border rounded-md px-3 py-2" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value.slice(0, 1000) })} placeholder="e.g. 2 cartons on back order, balance next week" data-testid="po-confirm-note" /></label>
                  {err && <p className="text-sm text-red-600 mt-3">{err}</p>}
                  <button onClick={confirm} disabled={busy} className="mt-6 w-full bg-emerald-600 hover:bg-emerald-700 transition-colors text-white font-semibold rounded-md h-12 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="po-confirm-submit">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}Confirm order
                  </button>
                  <p className="text-xs text-slate-500 text-center mt-3">Questions? Contact {po.company.name}{po.company.email ? ` at ${po.company.email}` : ''}{po.company.phone ? ` or ${po.company.phone}` : ''}.</p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
